/**
 * Merge Appearance draft pages into content for the preview shell.
 * Pure — safe to unit test without React.
 *
 * `{ removed: true }` takes a page off the preview (a release removes it, or a
 * frozen release does not list it); pages inside move up a level, as on the
 * release's own site.
 */

import { removePages } from '../pages/remove.js';

export function mergePreviewPage(content, id, page) {
    if (!content || !id || !page) return content;
    if (page.removed) {
        if (id === 'home' || !content.pages?.[id]) return content;
        const { pages } = removePages(content.pages, [id]);
        return { ...content, pages };
    }
    if (id === 'home') {
        return { ...content, home: { ...(content.home || {}), ...page } };
    }
    const current = content.pages?.[id];
    if (!current) {
        // A page created inside a release exists only as a draft until publish.
        if (!page.isNew || !page.slug) return content;
        return {
            ...content,
            pages: {
                ...content.pages,
                [id]: { ord: Object.keys(content.pages || {}).length, ...page, id },
            },
        };
    }
    // Keep routing identity (slug / legacy url) from the saved page. Draft slug
    // edits in Appearance must not break findPageByPath for the open URL.
    const merged = { ...current, ...page, slug: current.slug };
    if (current.url !== undefined) merged.url = current.url;
    return {
        ...content,
        pages: {
            ...content.pages,
            [id]: merged,
        },
    };
}

export function previewContent(content, previewPages) {
    if (!content || !previewPages) return content;
    const entries = Object.entries(previewPages).filter(([, page]) => page);
    if (!entries.length) return content;
    // Removals last, as going live does: a child's draft may still name the
    // removed parent, and has to move up with the rest.
    const ordered = [
        ...entries.filter(([, page]) => !page.removed),
        ...entries.filter(([, page]) => page.removed),
    ];
    return ordered.reduce(
        (next, [id, page]) => mergePreviewPage(next, id, page),
        content,
    );
}
